import { __ } from 'wp.i18n';
import { Fragment } from 'wp.element';
import PropTypes from 'prop-types';
import {
  InspectorControls,
  withColors,
  RichText,
  AlignmentToolbar,
  BlockControls,
  useBlockProps,
} from 'wp.blockEditor';
import {
  RangeControl,
  SelectControl,
  TextControl,
  PanelBody,
  CheckboxControl,
} from 'wp.components';
import classNames from 'classnames';

import Countup from './Countup';
import MarginControls from '../../components/controls/margin-controls';
import useUniqueId from '../../hooks/useUniqueId';
import getBlockId from '../../util/getBlockId';
import ResponsiveControl from '../../components/controls/responsive-control/ResponsiveControl';
import CountupStyle from './style';
import FontSizePickerLabel from '../../components/controls/text-controls/FontSizePickerLabel';
import BorderControls from '../../components/controls/border-controls';
import { getBorderCSSValue } from '../../components/controls/border-controls/helpers';
import BoxShadowControls from '../../components/controls/box-shadow-controls';
import { getBoxShadowCSSValue } from '../../components/controls/box-shadow-controls/helpers';
import PopoverColorControl from '../../components/controls/advanced-color-control/PopoverColorControl';
import BreakpointVisibilityControl from '../../components/controls/breakpoint-visibility-control';
import AuthVisibilityControl from '../../components/controls/auth-visibility-control';
import AnimationControls from '../../components/controls/animation-controls/AnimationControls';

const propTypes = {
  attributes: PropTypes.shape({
    uniqueId: PropTypes.string,
    startNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    endNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    animationDuration: PropTypes.number.isRequired,
    separator: PropTypes.string.isRequired,
    textFontSize: PropTypes.object,
    titleFontSize: PropTypes.object,
    textColor: PropTypes.string,
    customTextColor: PropTypes.string,
    prefix: PropTypes.string,
    suffix: PropTypes.string,
    inViewport: PropTypes.bool,
    blockMargin: PropTypes.shape({
      top: PropTypes.number,
      right: PropTypes.number,
      bottom: PropTypes.number,
      left: PropTypes.number,
    }),
  }).isRequired,
  setAttributes: PropTypes.func.isRequired,
  className: PropTypes.string,
  isSelected: PropTypes.bool.isRequired,
  clientId: PropTypes.string.isRequired,
};

const CountupEdit = ({
  attributes,
  setAttributes,
  isSelected,
  className,
  clientId,
}) => {
  const {
    uniqueId,
    startNumber,
    endNumber,
    animationDuration,
    inViewport,
    separator,
    textFontSize,
    titleFontSize,
    prefix,
    suffix,
    titleContent,
    align,
    textColor,
    titleColor,
    backgroundColor,
    titleTopMargin,
    blockBreakpointVisibility,
    blockAuthVisibility,
  } = attributes;

  useUniqueId({ attributes, setAttributes, clientId });
  const blockId = getBlockId(uniqueId);

  const blockProps = useBlockProps({
    id: blockId,
    className: classNames({
      [className]: !!className,
      [blockId]: true,
      [`wp-block-gutenbee-countup-align-${align}`]: !!align,
    }),
    style: {
      backgroundColor: backgroundColor || undefined,
      ...getBorderCSSValue({ attributes }),
      ...getBoxShadowCSSValue({ attributes }),
    },
  });

  return (
    <Fragment>
      <div {...blockProps}>
        <CountupStyle attributes={attributes} />

        <Countup
          {...attributes}
          startNumber={
            attributes.startNumber !== undefined &&
            attributes.startNumber !== ''
              ? Number(attributes.startNumber)
              : 0
          }
          endNumber={
            attributes.endNumber !== undefined && attributes.endNumber !== ''
              ? Number(attributes.endNumber)
              : 0
          }
          className="wp-block-gutenbee-countup-number"
        />

        <RichText
          tagName="p"
          value={titleContent}
          onChange={value => setAttributes({ titleContent: value })}
          className="wp-block-gutenbee-countup-title"
          placeholder={__('Write a title…')}
          style={{
            color: titleColor || undefined,
            marginTop:
              titleTopMargin != null ? `${titleTopMargin}px` : undefined,
          }}
        />
      </div>

      {isSelected && (
        <Fragment>
          <BlockControls>
            <AlignmentToolbar
              value={align}
              onChange={value => setAttributes({ align: value || 'left' })}
            />
          </BlockControls>

          <InspectorControls>
            <PanelBody title={__('Settings')} initialOpen>
              <TextControl
                type="number"
                label={__('Start Number')}
                value={startNumber}
                onChange={value =>
                  setAttributes({
                    startNumber: value !== '' ? value : '0',
                  })
                }
                __nextHasNoMarginBottom
                __next40pxDefaultSize
              />

              <TextControl
                type="number"
                label={__('End Number')}
                value={endNumber}
                onChange={value =>
                  setAttributes({
                    endNumber: value !== '' ? value : '0',
                  })
                }
                __nextHasNoMarginBottom
                __next40pxDefaultSize
              />

              <TextControl
                label={__('Prefix')}
                value={prefix}
                onChange={value => setAttributes({ prefix: value })}
                __nextHasNoMarginBottom
                __next40pxDefaultSize
              />

              <TextControl
                label={__('Suffix')}
                value={suffix}
                onChange={value => setAttributes({ suffix: value })}
                __nextHasNoMarginBottom
                __next40pxDefaultSize
              />

              <RangeControl
                label={__('Animation Duration (seconds)')}
                value={animationDuration}
                min={0}
                max={100}
                step={0.5}
                onChange={value => setAttributes({ animationDuration: value })}
                __nextHasNoMarginBottom
                __next40pxDefaultSize
              />

              <CheckboxControl
                label={__('Start counting when in viewport')}
                value="on"
                checked={inViewport}
                onChange={value => {
                  setAttributes({ inViewport: value });
                }}
                __nextHasNoMarginBottom
              />

              <SelectControl
                label={__('Separator')}
                value={separator}
                onChange={value => setAttributes({ separator: value })}
                __nextHasNoMarginBottom
                __next40pxDefaultSize
                options={[
                  { value: '', label: __('None') },
                  { value: ',', label: __('Comma ,') },
                  { value: '.', label: __('Dot .') },
                  { value: ' ', label: __('Space') },
                ]}
              />

              <ResponsiveControl>
                {breakpoint => (
                  <FontSizePickerLabel
                    label={__('Text Font Size')}
                    value={
                      textFontSize[breakpoint] !== undefined &&
                      textFontSize[breakpoint] !== ''
                        ? textFontSize[breakpoint]
                        : undefined
                    }
                    onChange={value =>
                      setAttributes({
                        textFontSize: {
                          ...textFontSize,
                          [breakpoint]: value != null ? value : '',
                        },
                      })
                    }
                  />
                )}
              </ResponsiveControl>

              <RangeControl
                label={__('Title Top Margin')}
                value={titleTopMargin}
                onChange={value => {
                  setAttributes({
                    titleTopMargin: value != null ? value : undefined,
                  });
                }}
                min={0}
                max={200}
                allowReset
                __nextHasNoMarginBottom
                __next40pxDefaultSize
              />

              <ResponsiveControl>
                {breakpoint => (
                  <FontSizePickerLabel
                    label={__('Title Font Size')}
                    value={
                      titleFontSize[breakpoint] !== undefined &&
                      titleFontSize[breakpoint] !== ''
                        ? titleFontSize[breakpoint]
                        : undefined
                    }
                    onChange={value =>
                      setAttributes({
                        titleFontSize: {
                          ...titleFontSize,
                          [breakpoint]: value != null ? value : '',
                        },
                      })
                    }
                  />
                )}
              </ResponsiveControl>
            </PanelBody>

            <PanelBody title={__('Block Appearance')} initialOpen={false}>
              <PopoverColorControl
                label={__('Text Color')}
                value={textColor || ''}
                defaultValue={textColor || ''}
                onChange={value => setAttributes({ textColor: value })}
              />

              <PopoverColorControl
                label={__('Title Color')}
                value={titleColor || ''}
                defaultValue={titleColor || ''}
                onChange={value => setAttributes({ titleColor: value })}
              />

              <PopoverColorControl
                label={__('Background Color')}
                value={backgroundColor || ''}
                defaultValue={backgroundColor || ''}
                onChange={value => setAttributes({ backgroundColor: value })}
              />

              <BorderControls
                attributes={attributes}
                setAttributes={setAttributes}
              />

              <BoxShadowControls
                attributes={attributes}
                setAttributes={setAttributes}
              />

              <ResponsiveControl>
                {breakpoint => (
                  <MarginControls
                    label={__('Padding (px)')}
                    attributeKey="blockPadding"
                    attributes={attributes}
                    setAttributes={setAttributes}
                    breakpoint={breakpoint}
                  />
                )}
              </ResponsiveControl>

              <ResponsiveControl>
                {breakpoint => (
                  <MarginControls
                    label={__('Margin (px)')}
                    attributeKey="blockMargin"
                    attributes={attributes}
                    setAttributes={setAttributes}
                    breakpoint={breakpoint}
                  />
                )}
              </ResponsiveControl>
            </PanelBody>

            <PanelBody title={__('Visibility Settings')} initialOpen={false}>
              <BreakpointVisibilityControl
                values={blockBreakpointVisibility}
                onChange={values => {
                  setAttributes({
                    blockBreakpointVisibility: values,
                  });
                }}
              />

              <AuthVisibilityControl
                values={blockAuthVisibility}
                onChange={values => {
                  setAttributes({
                    blockAuthVisibility: values,
                  });
                }}
              />
            </PanelBody>

            {__GUTENBEE_SETTINGS__.plugin.settings[
              'active_animation-controls'
            ] && (
              <PanelBody
                icon={!!attributes.animation?.type && 'saved'}
                title={__('Animation')}
                initialOpen={false}
              >
                <AnimationControls
                  attributes={{
                    ...attributes.animation,
                    duration:
                      attributes.animation?.duration !== undefined &&
                      attributes.animation?.duration !== ''
                        ? Number(attributes.animation.duration)
                        : undefined,
                    delay:
                      attributes.animation?.delay !== undefined &&
                      attributes.animation?.delay !== ''
                        ? Number(attributes.animation.delay)
                        : undefined,
                  }}
                  setAttributes={setAttributes}
                />
              </PanelBody>
            )}
          </InspectorControls>
        </Fragment>
      )}
    </Fragment>
  );
};

CountupEdit.propTypes = propTypes;

export default withColors({ textColor: 'color' })(CountupEdit);
